import { useEffect, useMemo, useState } from "react";
import { Wallet, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, FileSearch } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, formatIDR, DIVISION_COLOR, buildKetidakpastianTooltip } from "@/lib/constants";
import StaffCashLedgerDialog from "@/components/StaffCashLedgerDialog";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";

const COLS = [
  { key: "user_name", label: "Staf" },
  { key: "division", label: "Divisi" },
  { key: "total_transferred", label: "Dana Diterima", numeric: true,
    hint: "Σ transfer_amount terkonfirmasi (termasuk Rp 0 yang sah) + estimasi dana historis. Nilai identik dengan Rekonsiliasi Kas Staf." },
  { key: "total_actual_verified", label: "Realisasi Terverifikasi", numeric: true,
    hint: "Σ item.actual_amount yang lolos Verifikasi 1 (verifikasi pengeluaran, per item). Verifikasi 2 (penentuan COA) tidak memengaruhi nilai ini." },
  { key: "total_returned", label: "Pengembalian Kas Terverifikasi", numeric: true,
    hint: "Σ amount pengembalian kas status terverifikasi. Pengembalian yang menunggu verifikasi tidak memotong saldo ini." },
  { key: "balance", label: "Saldo Pertanggungjawaban", numeric: true,
    hint: "Dana Diterima − Realisasi Terverifikasi − Pengembalian Kas Terverifikasi. Bila ada ketidakpastian (status verifikasi belum selaras atau nominal historis belum diketahui), nilai formal belum dapat dipastikan." },
  { key: "last_activity_at", label: "Aktivitas Terakhir" },
];

/**
 * StaffCashRecapWidget — tabel rekap saldo kas semua staff untuk BOD / FAT.
 * Data: GET /api/petty-cash/staff-summary (reviewer-only).
 * (User request 08-Feb-2026): "Rekap Kas Semua Staff: Tabel di dashboard BOD/FAT
 * menampilkan saldo tiap staff (transferred/verified/returned/sisa)".
 */
export default function StaffCashRecapWidget() {
  const { user } = useAuth();
  const { visibilityScope } = useAccess();
  const scope = visibilityScope ? visibilityScope(user, "petty_cash") : "all";
  const title = scope === "own"
    ? "Rekap Kas Saya"
    : (scope === "division" || scope === "division_lower")
      ? "Rekap Kas Divisi"
      : "Rekap Kas Semua Staf";
  const subtitle = scope === "own"
    ? "Ringkasan saldo pertanggungjawaban petty cash Anda"
    : (scope === "division" || scope === "division_lower")
      ? "Ringkasan saldo pertanggungjawaban petty cash staf se-divisi Anda"
      : "Ringkasan saldo pertanggungjawaban petty cash setiap staf";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "balance", dir: "desc" });
  const [q, setQ] = useState("");
  const [drillUserId, setDrillUserId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/petty-cash/staff-summary")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = needle
      ? rows.filter((r) => r.user_name.toLowerCase().includes(needle) || (r.division || "").toLowerCase().includes(needle))
      : rows;
    if (sort.key) {
      const dir = sort.dir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = a[sort.key]; const vb = b[sort.key];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number") return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    return list;
  }, [rows, q, sort]);

  const totals = useMemo(() => {
    const anyUncertain = filtered.some((r) => r.engine?.saldo_pj_completeness === "belum_dapat_dipastikan");
    return filtered.reduce((acc, r) => ({
      transferred: acc.transferred + (r.total_transferred || 0),
      verified: acc.verified + (r.total_actual_verified || 0),
      returned: acc.returned + (r.total_returned || 0),
      balance: acc.balance + (r.balance || 0),
    }), { transferred: 0, verified: 0, returned: 0, balance: 0, anyUncertain });
  }, [filtered]);

  const clickHeader = (col) => {
    if (sort.key !== col.key) setSort({ key: col.key, dir: col.numeric ? "desc" : "asc" });
    else if (sort.dir === (col.numeric ? "desc" : "asc")) setSort({ key: col.key, dir: col.numeric ? "asc" : "desc" });
    else setSort({ key: "balance", dir: "desc" });
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="widget-staff-cash-recap">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-50 border border-emerald-200 flex items-center justify-center">
            <Wallet size={15} className="text-emerald-700" />
          </div>
          <div>
            <h3 className="font-display text-base tracking-tight text-[#0B1D3A]" data-testid="staff-cash-recap-title">{title}</h3>
            <p className="text-[10px] text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/finance-report/rekonsiliasi-kas-staf"
            data-testid="recap-open-pc-recon"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline"
            title="Buka halaman Rekonsiliasi Kas Staf (12 metrik + transaksi pembentuk)"
          >
            <FileSearch size={11} /> Rekonsiliasi Kas Staf
          </Link>
          <input
            data-testid="input-recap-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari staf / divisi…"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-none focus:outline-none focus:border-[#FF5A1F] w-56"
          />
          <button data-testid="btn-recap-refresh" onClick={load} className="p-1.5 text-slate-500 hover:text-[#0B1D3A] border border-slate-200 hover:border-slate-400" title="Refresh"><RefreshCw size={13} /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              {COLS.map((col) => {
                const active = sort.key === col.key;
                const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th key={col.key} className={`px-3 py-2 overline text-slate-600 whitespace-nowrap ${col.numeric ? "text-right" : ""}`}>
                    <button
                      onClick={() => clickHeader(col)}
                      data-testid={`recap-sort-${col.key}`}
                      title={col.hint || undefined}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider text-[10px] font-semibold ${active ? "text-[#0B1D3A]" : "text-slate-600"} hover:text-[#0B1D3A]`}
                    >
                      {col.label} <Icon size={9} className={active ? "text-[#FF5A1F]" : "text-slate-400"} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody data-testid="recap-body">
            {loading && <tr><td colSpan={COLS.length} className="px-3 py-6 text-center text-slate-400 italic">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={COLS.length} className="px-3 py-6 text-center text-slate-400 italic">Belum ada staf dengan aktivitas kas.</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const divCls = DIVISION_COLOR[r.division] || "bg-slate-100 text-slate-700 border-slate-300";
              return (
                <tr
                  key={r.user_id}
                  className="border-b border-slate-100 hover:bg-emerald-50 cursor-pointer transition-colors"
                  onClick={() => setDrillUserId(r.user_id)}
                  data-testid={`recap-row-${r.user_id}`}
                  title="Klik untuk lihat detail transfer, realisasi terverifikasi, dan pengembalian kas"
                >
                  <td className="px-3 py-2 font-medium text-[#0B1D3A]">
                    {r.user_name}
                    {r.engine?.has_legacy && (
                      <span
                        className="ml-1.5 inline-flex px-1 py-0 text-[8px] uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-800 align-middle"
                        data-testid={`recap-legacy-badge-${r.user_id}`}
                        title={`Estimasi dana historis: ${formatIDR(r.engine?.dana_diterima_estimasi_legacy ?? 0)} — nominal transfer historis belum diketahui.`}
                      >historis</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${divCls}`}>{r.division || "-"}</span>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right text-slate-700">
                    {formatIDR(r.total_transferred)}
                    {r.engine?.has_legacy && (
                      <div className="text-[9px] text-amber-700 tabular-nums" data-testid={`recap-legacy-amt-${r.user_id}`}>
                        terkonfirmasi {formatIDR(r.engine?.dana_diterima_terkonfirmasi ?? 0)} · estimasi historis {formatIDR(r.engine?.dana_diterima_estimasi_legacy ?? 0)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right text-slate-700">− {formatIDR(r.total_actual_verified)}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-slate-700">− {formatIDR(r.total_returned)}</td>
                  <td className={`px-3 py-2 tabular-nums text-right font-bold ${r.balance > 0 ? "text-emerald-700" : r.balance < 0 ? "text-rose-700" : "text-slate-400"}`} data-testid={`recap-balance-${r.user_id}`}>
                    {formatIDR(r.balance)}
                    {r.engine?.saldo_pj_completeness === "belum_dapat_dipastikan" && (
                      <div
                        className="text-[9px] font-normal text-rose-700"
                        data-testid={`recap-balance-uncertain-${r.user_id}`}
                        title={`${buildKetidakpastianTooltip(r.engine)}${r.engine?.saldo_pj_strict != null ? ` Pembanding — tanpa estimasi historis: ${formatIDR(r.engine.saldo_pj_strict)} · dengan estimasi historis: ${formatIDR(r.engine?.saldo_pj_termasuk_estimasi ?? 0)}.` : ""}`}
                      >Belum dapat dipastikan</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-[10px] tabular-nums">{r.last_activity_at ? fmtDate(r.last_activity_at) : <span className="text-slate-300">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-300 font-bold">
              <tr>
                <td colSpan={2} className="px-3 py-2 uppercase text-[10px] tracking-wider text-slate-700">
                  {scope === "own" ? `Total (${filtered.length} entri)` : `Total (${filtered.length} staf)`}
                </td>
                <td className="px-3 py-2 tabular-nums text-right text-slate-800">{formatIDR(totals.transferred)}</td>
                <td className="px-3 py-2 tabular-nums text-right text-slate-800">− {formatIDR(totals.verified)}</td>
                <td className="px-3 py-2 tabular-nums text-right text-slate-800">− {formatIDR(totals.returned)}</td>
                <td className="px-3 py-2 tabular-nums text-right text-emerald-700" data-testid="recap-total-balance">
                  {formatIDR(totals.balance)}
                  {filtered.some((r) => r.engine?.saldo_pj_completeness === "belum_dapat_dipastikan") && (
                    <div
                      className="text-[9px] font-normal text-rose-700"
                      data-testid="recap-total-uncertain"
                      title="Total mewarisi ketidakpastian dari salah satu staf. Lihat badge 'Belum dapat dipastikan' di baris staf terkait."
                    >Sementara — belum dapat dipastikan</div>
                  )}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {drillUserId && (
        <StaffCashLedgerDialog
          userId={drillUserId}
          open={!!drillUserId}
          onOpenChange={(o) => { if (!o) setDrillUserId(null); }}
        />
      )}
    </div>
  );
}

